import { CardContent, Grid, Paper } from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'
import Chart from 'react-google-charts'
import { UseStore } from 'zustand'
import { requestActivity, withTooltip } from '../../core'
import { TagsAuditReport } from '../../core/controls'
import { utcDateString } from '../../core/date-time'
import { FormApi, formApi } from '../../core/form'
import { OrganizationApi } from '../../core/organization-api'
import { darkOrange, green, lightGreen, orange, yellow } from '../../core/theme'
import { pageApi } from '../coding-list/visit-list-api-activity'
import { tabData } from '../coding-list/visit-page-activity'
import { auditReportCall } from './audit-report-api'
import { auditWorksheetSearchApi } from './audit-worhsheet-search-api'
import { Breakdown, BreakdownValue, formatBreakdownValue, getBreakdown } from './audit-types'
import { Popover } from '@mui/material'

export type AuditResult = {
  success?: boolean
  locationCodes?: string[]
  departmentCodes?: string[]
  worksheetTypes?: string[]
  providers?: number[]
  audited?: number[]
  batches?: number[]
  startDate?: Date
  endDate?: Date
  page?: number
  pageSize?: number
  totalCount?: number
  domain?: AuditDomain
  documentVersion?: number
  result: resultProps
  message?: string
  error?: string
}

export type resultProps = {
  'ICD-10 CM Accuracy'?: {
    values: AuditResultValues[]
    title: string
    color?: string
  }
  'Overall Accuracy'?: {
    values: AuditResultValues[]
    title: string
    color?: string
  }
  'CPT Accuracy': {
    values: AuditResultValues[]
    title: string
    color?: string
  }
  'CPT Errors': {
    values: AuditResultValues[]
    title: string
    color?: string
  }
  'ICD-10 Errors': {
    values: AuditResultValues[]
    title: string
    color?: string
  }
  'Clean Claim Accuracy': {
    values: AuditResultValues[]
    title: string
    color?: string
  }
}

type AuditDoaminField = {
  id: number | string
  label: string
  description?: string
}

export type AuditDomain = {
  'Providers'?: AuditDoaminField[]
  'Audited'?: AuditDoaminField[]
  'Audit Batches'?: AuditDoaminField[]
}

type Field = {
  fieldName: string
  stringFilter?: string
  integerFilter?: number
  floatFilter?: number
  booleanFilter?: boolean | [boolean, boolean]
  startDate?: Date | string
  endDate?: Date | string
}

type Filters = {
  predicate?: 'AND' | 'OR'
  fields?: Field[]
}

type AuditResultValues = {
  label: string
  units: string
  value: number
  filter?: Filters
  order?: number
  correct?: number
  incorrect?: number
  total?: number
}

type AuditReportProps = {
  org?: UseStore<OrganizationApi>
}

export function AuditedReport(props: AuditReportProps) {
  const { org } = props

  const selection = org((s) => s.selection)
  const tabValue = tabData.getState().tab
  const result = auditReportCall.data((s) => s.audited.result)
  const scatterResult = auditReportCall.data((s) => s.audited.scatter)
  const domain = auditReportCall.data((s) => s.audited.domain)
  const loading = requestActivity((s) => s.active)

  function updateResponse(id?: any) {
    const departmentCode = auditReportCall.data.getState().departmentCodes
    const locationCode = auditReportCall.data.getState().locationCodes
    const worksheetType = auditReportCall.data.getState().worksheetTypes
    const startDate = auditReportCall.data.getState().startDate
    const endDate = auditReportCall.data.getState().endDate
    let auditId =
      tabData.getState().tab === 0
        ? auditReportCall.data.getState().audited.auditId
        : tabData.getState().tab === 1
        ? auditReportCall.data.getState().auditor.auditId
        : null
    let providerId =
      tabData.getState().tab === 0
        ? auditReportCall.data.getState().audited.providerId
        : tabData.getState().tab === 1
        ? auditReportCall.data.getState().auditor.providerId
        : null
    if (id?.title == 'auditId') {
      auditId = id.value
    } else if (id?.title == 'providerId') {
      providerId = id.value
    }

    auditReportCall.actions.getAuditedResults({
      departmentCode,
      locationCode,
      worksheetType,
      startDate,
      endDate,
      auditId,
      providerId,
    })
  }

  const form: FormApi<{
    batchControl: any
    providerControl: any
    coderControl: any
  }> = useRef(
    formApi({
      values: {
        batchControl: [],
        providerControl: [],
        coderControl: [],
      },
      fields: {
        batchControl: {
          required: true,
          list: domain ? domain['Audit Batches'] : [],
          listLabel: (v) => v.label,
          listValue: (v) => v.id,
          style: { width: undefined },
          onChange: (v) => {
            let resp = { title: 'auditId', value: [] }
            v?.map((item) => {
              resp.value.push(item.name)
            })
            checkRefresh() && updateResponse(resp)
          },
        },
        providerControl: {
          required: true,
          list: [],
          listLabel: (v) => v.label,
          style: { width: undefined },
          onChange: (v) => {
            let resp = { title: 'providerId', value: [] }
            v?.map((item) => {
              resp.value.push(item.name)
            })

            checkRefresh() && updateResponse(resp)
          },
        },
        coderControl: {
          required: true,
          list: [],
          listLabel: (v) => v.label,
          style: { width: undefined },
          onChange: (v) => {},
        },
      },
      valid: () => true,
      onSubmit: (v) => {
        console.log('V ', v)
      },
    }),
  ).current

  function checkRefresh() {
    if (
      auditReportCall.data.getState().audited.reload === false &&
      auditReportCall.data.getState().audited.clear === false
    ) {
      return true
    }
    return false
  }

  const { batchControl, providerControl, coderControl } = form.controls

  let tempBatch = []
  let tempProviders = []
  let tempCoders = []

  domain != null &&
    domain['Audit Batches'] &&
    domain['Audit Batches'].map((item) => {
      tempBatch.push({ label: item.label, name: item.id })
    })
  domain != null &&
    domain['Providers'] &&
    domain['Providers'].map((item) => {
      tempProviders.push({ label: item.label, name: item.id })
    })
  domain?.Audited &&
    domain.Audited.map((item) => {
      tempCoders.push({ label: item.label, name: item.id })
    })

  batchControl.list.setState({ value: tempBatch })
  providerControl.list.setState({ value: tempProviders })
  coderControl.list.setState({ value: tempCoders })

  batchControl.variant = 'outlined'
  providerControl.variant = 'outlined'
  coderControl.variant = 'outlined'

  useEffect(() => {
    let departments: string[] = []
    selection.location.departments.map((i) => {
      departments.push(i.code)
    })
    if (tabValue === 0 && auditReportCall.data.getState().audited.reload === true) {
      let prop = auditReportCall.data.getState().audited
      prop.auditId = []
      prop.providerId = []
      prop.reload = false
      auditReportCall.data.setState({ audited: prop })
      batchControl.value.getState().reset()
      providerControl.value.getState().reset()
      updateResponse()
    }
  }, [tabValue])

  if (auditReportCall.data((s) => s.audited.clear) === true) {
    batchControl.value.getState().reset()
    providerControl.value.getState().reset()
    let prop = auditReportCall.data.getState().audited
    prop.auditId = []
    prop.providerId = []
    prop.clear = false
    auditReportCall.data.setState({ audited: prop })
  }

  return (
    <>
      <Grid
        style={{
          width: '75%',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          paddingBottom: 7,
          paddingTop: 15,
        }}
      >
        <Grid
          item
          container
          direction="row"
          style={{
            flexWrap: 'nowrap',
            paddingBottom: 6,
            paddingTop: 6,
            width: '100%',
          }}
          alignItems="flex-start"
        >
          <Grid item style={{ flex: 1, paddingRight: 30 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Audit Batches:</div>
            <TagsAuditReport {...batchControl} />
          </Grid>
          <Grid item style={{ flex: 1, paddingRight: 30 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Providers:</div>

            <TagsAuditReport {...providerControl} />
          </Grid>
          {tabData.getState().tab === 1 && (
            <Grid item style={{ flex: 1, paddingRight: 30 }}>
              <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Coders:</div>

              <TagsAuditReport {...coderControl} />
            </Grid>
          )}
        </Grid>
      </Grid>
      {result ? (
        <div style={{ minHeight: 250 }}>
          <Report {...result} />
        </div>
      ) : loading ? (
        <div
          style={{
            minHeight: 250,
            width: '75%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          Loading...
        </div>
      ) : (
        <div
          style={{
            minHeight: 250,
            width: '75%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          No data found!
        </div>
      )}
      {scatterResult && (
        <div style={{ minHeight: 250 }}>
          <ScatterComponent result={scatterResult as any} title="Audit Score by Service Date" />
        </div>
      )}
    </>
  )
}

export function AuditorReport(props: AuditReportProps) {
  const { org } = props

  const selection = org((s) => s.selection)
  const tabValue = tabData.getState().tab
  const result = auditReportCall.data((s) => s.auditor.result)
  const scatterResult = auditReportCall.data((s) => s.auditor.scatter)
  const domain = auditReportCall.data((s) => s.auditor.domain)
  const loading = requestActivity((s) => s.active)

  function updateResponse(id?: any) {
    const departmentCode = auditReportCall.data.getState().departmentCodes
    const locationCode = auditReportCall.data.getState().locationCodes
    const worksheetType = auditReportCall.data.getState().worksheetTypes
    const startDate = auditReportCall.data.getState().startDate
    const endDate = auditReportCall.data.getState().endDate
    let auditId =
      tabData.getState().tab === 0
        ? auditReportCall.data.getState().audited.auditId
        : tabData.getState().tab === 1
        ? auditReportCall.data.getState().auditor.auditId
        : null
    let providerId =
      tabData.getState().tab === 0
        ? auditReportCall.data.getState().audited.providerId
        : tabData.getState().tab === 1
        ? auditReportCall.data.getState().auditor.providerId
        : null
    let auditedId = tabData.getState().tab === 1 ? auditReportCall.data.getState().auditor.auditedId : null

    if (id?.title == 'auditId') {
      auditId = id.value
    } else if (id?.title == 'providerId') {
      providerId = id.value
    } else if (id?.title == 'audited') {
      auditedId = id.value
    }

    auditReportCall.actions.getAuditorResults({
      departmentCode,
      locationCode,
      worksheetType,
      startDate,
      endDate,
      auditId,
      providerId,
      auditedId,
    })
  }

  const form: FormApi<{
    batchControl: any
    providerControl: any
    coderControl: any
  }> = useRef(
    formApi({
      values: {
        batchControl: [],
        providerControl: [],
        coderControl: [],
      },
      fields: {
        batchControl: {
          required: true,
          list: domain && domain['Audit Batches'] ? domain['Audit Batches'] : [],
          listLabel: (v) => v.label,
          listValue: (v) => v.id,
          style: { width: undefined },
          onChange: (v) => {
            let resp = { title: 'auditId', value: [] }
            v?.map((item) => {
              resp.value.push(item.name)
            })

            checkRefresh() && updateResponse(resp)
          },
        },
        providerControl: {
          required: true,
          list: [],
          listLabel: (v) => v.label,
          style: { width: undefined },
          onChange: (v) => {
            let resp = { title: 'providerId', value: [] }
            v?.map((item) => {
              resp.value.push(item.name)
            })

            checkRefresh() && updateResponse(resp)
          },
        },
        coderControl: {
          required: true,
          list: [],
          listLabel: (v) => v.label,
          style: { width: undefined },
          onChange: (v) => {
            let resp = { title: 'audited', value: [] }
            v?.map((item) => {
              resp.value.push(item.name)
            })

            checkRefresh() && updateResponse(resp)
          },
        },
      },
      valid: () => true,
      onSubmit: (v) => {
        console.log('V ', v)
      },
    }),
  ).current

  function checkRefresh() {
    if (
      auditReportCall.data.getState().auditor.reload === false &&
      auditReportCall.data.getState().auditor.clear === false
    ) {
      return true
    }
    return false
  }

  const { batchControl, providerControl, coderControl } = form.controls

  let tempBatch = []
  let tempProviders = []
  let tempCoders = []

  domain != null &&
    domain['Audit Batches'] &&
    domain['Audit Batches'].map((item) => {
      tempBatch.push({ label: item.label, name: item.id })
    })
  domain != null &&
    domain['Providers'] &&
    domain['Providers'].map((item) => {
      tempProviders.push({ label: item.label, name: item.id })
    })
  domain?.Audited &&
    domain.Audited.map((item) => {
      tempCoders.push({ label: item.label, name: item.id })
    })

  batchControl.list.setState({ value: tempBatch })
  providerControl.list.setState({ value: tempProviders })
  coderControl.list.setState({ value: tempCoders })

  batchControl.variant = 'outlined'
  providerControl.variant = 'outlined'
  coderControl.variant = 'outlined'

  useEffect(() => {
    let departments: string[] = []
    selection.location.departments.map((i) => {
      departments.push(i.code)
    })

    if (tabValue === 1 && auditReportCall.data.getState().auditor.reload === true) {
      batchControl.value.getState().reset()
      providerControl.value.getState().reset()
      coderControl.value.getState().reset()
      let prop = auditReportCall.data.getState().auditor
      prop.reload = false
      prop.auditId = []
      prop.providerId = []
      prop.auditedId = []
      auditReportCall.data.setState({ auditor: prop })
      updateResponse()
    }
  }, [tabValue])

  if (auditReportCall.data((s) => s.auditor.clear) === true) {
    batchControl.value.getState().reset()
    providerControl.value.getState().reset()
    coderControl.value.getState().reset()
    let prop = { ...auditReportCall.data.getState().auditor }
    prop.auditId = []
    prop.providerId = []
    prop.auditedId = []
    prop.clear = false
    auditReportCall.data.setState({ auditor: prop })
  }

  return (
    <>
      <Grid
        style={{
          width: '75%',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          paddingBottom: 7,
          paddingTop: 15,
        }}
      >
        <Grid
          item
          container
          direction="row"
          style={{
            flexWrap: 'nowrap',
            paddingBottom: 6,
            paddingTop: 6,
            width: '100%',
          }}
          alignItems="flex-start"
        >
          <Grid item style={{ flex: 1, paddingRight: 30 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Audit Batches:</div>
            {<TagsAuditReport {...batchControl} />}
          </Grid>
          <Grid item style={{ flex: 1, paddingRight: 30 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Providers:</div>

            <TagsAuditReport {...providerControl} />
          </Grid>
          <Grid item style={{ flex: 1, paddingRight: 30 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Coders:</div>

            <TagsAuditReport {...coderControl} />
          </Grid>
        </Grid>
      </Grid>

      {result ? (
        <div style={{ minHeight: 250 }}>
          <Report {...result} />
        </div>
      ) : loading ? (
        <div
          style={{
            minHeight: 250,
            width: '75%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          Loading...
        </div>
      ) : (
        <div
          style={{
            minHeight: 250,
            width: '75%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          No data found!
        </div>
      )}
      {scatterResult && (
        <div style={{ minHeight: 250 }}>
          <ScatterComponent result={scatterResult as any} title="Audit Score by Service Date" />
        </div>
      )}
    </>
  )
}

function Report(props: resultProps) {
  const icd_10 = props['ICD-10 CM']
  icd_10 ? (icd_10.color = yellow) : ''
  icd_10 ? (icd_10.title = 'ICD-10 CM Accuracy') : ''

  const overall_accuracy = props['Overall Accuracy']
  overall_accuracy ? (overall_accuracy.color = green) : ''
  overall_accuracy ? (overall_accuracy.title = 'Overall Accuracy') : ''

  const cpt_accuracy = props['CPT']
  cpt_accuracy ? (cpt_accuracy.color = darkOrange) : ''
  cpt_accuracy ? (cpt_accuracy.title = 'CPT Accuracy') : ''

  const icd_10_error = props['ICD-10 Errors']
  icd_10_error ? (icd_10_error.color = orange) : ''

  const cpt_errors = props['CPT Errors']
  cpt_errors ? (cpt_errors.color = orange) : ''

  const clean_claims = props['Clean Claim Accuracy']
  clean_claims ? (clean_claims.title = 'Clean Claim Accuracy') : ''
  clean_claims ? (clean_claims.color = '#1bb71f') : ''

  return (
    <>
      <Grid
        style={{
          width: '75%',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          paddingBottom: 7,
          paddingTop: 15,
        }}
      >
        {clean_claims && (
          <GridReportCard
            labelLeft="Count"
            listLabelPercentage={'Accuracy Percentage'}
            listLabelAll={'Total Audited Worksheets'}
            listLabelAwarded={'Total Clean Claim Worksheets'}
            {...clean_claims}
          />
        )}
        {icd_10 && (
          <GridReportCard
            labelLeft="Points"
            {...icd_10}
            listLabelAll="Total Possible"
            listLabelAwarded="Total Awarded"
            listLabelPercentage="Accuracy Percentage"
          />
        )}
        {cpt_accuracy && (
          <GridReportCard
            labelLeft="Points"
            {...cpt_accuracy}
            listLabelAll="Total Possible"
            listLabelAwarded="Total Awarded"
            listLabelPercentage="Accuracy Percentage"
          />
        )}
        {overall_accuracy && (
          <GridReportCard
            labelLeft="Points"
            listLabelPercentage={'Accuracy Percentage'}
            listLabelAll={'Total Possible'}
            listLabelAwarded={'Total Awarded'}
            {...overall_accuracy}
          />
        )}
      </Grid>
      <Grid
        style={{
          width: '80%',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          paddingBottom: 7,
          paddingTop: 15,
        }}
      >
        <HorizontalChart {...icd_10_error} title="ICD-10 Errors" />
        <HorizontalChart {...cpt_errors} title="CPT Errors" />
      </Grid>
    </>
  )
}

type AuditScatterValues = {
  worksheetId: string | number
  locationCode: string | string[]
  departmentCode: string | string[]
  worksheetType: string | string[]
  accountNumber: string
  mrn: string
  serviceDate: Date
  totalPossiblePoints: number
  totalPoints: number
  audited: {
    userId: string
    workflow: string
    lastName: string
    firstName: string
    email: string
  }
  percentage: number
  filter?: {
    predicate: string
    fields: any[]
  }
}
type AuditResultsProps = {
  values: AuditResultValues[]
  listLabelPercentage: string
  listLabelAll: string
  listLabelAwarded: string
  title?: string
  color?: string
  labelLeft?: string
}

function onFilter(filter: Filters) {
  if (!filter) filter = { predicate: 'AND', fields: [] }

  let temp = auditWorksheetSearchApi()
  pageApi.setState({ pageNumber: 0, pageSize: 20 })
  temp.data.setState((s) => {
    s.query.page = 0
  })

  if (tabData.getState().tab === 0) {
    temp.data.setState({ isAudited: true })
    const { auditId, providerId } = auditReportCall.data.getState().audited
    const props = {
      auditId: auditId ? (auditId as any) : null,
      providerId: providerId ? (providerId as any) : null,
    }
    filter && temp.actions.sendRawQuery(filter, props)
  } else {
    temp.data.setState({ isAudited: false })
    const { auditId, providerId, auditedId } = auditReportCall.data.getState().auditor
    const props = {
      auditId: auditId ? (auditId as any) : null,
      providerId: providerId ? (providerId as any) : null,
      auditedId: auditedId ? (auditedId as any) : null,
    }
    filter && temp.actions.sendRawQuery(filter, props)
  }

  tabData.setState({ tab: 2 })
}

function GridReportCard(props: AuditResultsProps) {
  const { values, title, color, listLabelPercentage, listLabelAll, listLabelAwarded, labelLeft } = props

  const percentage = values.find((v) => v.label === listLabelPercentage) || null
  const all = values.find((v) => v.label === listLabelAll) || null
  const awarded = values.find((v) => v.label === listLabelAwarded) || null
  const breakdown = getBreakdown(values as any)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const onPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const onPopoverClose = (_e) => {
    // TODO handle popover close if hover needs events
    // console.info('e', _e)
    setAnchorEl(null)
  }
  console.info('props', breakdown)

  return (
    <>
      <Grid item style={{ flex: 1, paddingRight: 30, height: 120 }}>
        <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>{title}</div>
        <Paper
          elevation={2}
          style={{ height: 63 }}
          onMouseEnter={breakdown && onPopoverOpen}
          onMouseLeave={breakdown && onPopoverClose}
        >
          <CardContent>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              style={{ height: '100%', minWidth: 150, flexWrap: 'nowrap' }}
            >
              {percentage &&
                withTooltip(
                  percentage.label,
                  <Grid item>
                    <div
                      onClick={() => onFilter(percentage.filter)}
                      style={{
                        cursor: 'pointer',
                        color: color,
                        fontSize: 24,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        marginTop: -6,
                      }}
                    >
                      {values && percentage && typeof percentage.value === 'number'
                        ? percentage.value > 1
                          ? Math.round(percentage.value) + ' %'
                          : Math.round(percentage.value * 100) + ' %'
                        : 0 + ' %'}
                    </div>
                  </Grid>,
                  'bottom',
                )}
              {awarded && (
                <Grid item container direction="column" alignItems="flex-end">
                  <div style={{ color: '#999', fontSize: 10 }}>{labelLeft}: Error / Total</div>
                  <div style={{ cursor: 'pointer', fontSize: 16, fontWeight: 550 }}>
                    {withTooltip(
                      awarded.label,
                      <span onClick={() => onFilter(awarded.filter)}>
                        {values && awarded && typeof awarded.value === 'number' ? awarded.value : 0}
                      </span>,
                      'bottom',
                    )}{' '}
                    /&nbsp;
                    {withTooltip(
                      all.label,
                      <span onClick={!all ? undefined : () => onFilter(all.filter)}>
                        {values && all && typeof all.value === 'number' ? all.value : 0}
                      </span>,
                      'bottom',
                    )}
                  </div>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Paper>
      </Grid>
      {<DistributionHover {...breakdown} open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onPopoverClose} />}
    </>
  )
}

function DistributionReportCard(props: Breakdown) {
  const { label } = props
  const values = props.value || []

  return (
    <Grid
      container
      direction="row"
      spacing={0}
      alignItems="center"
      style={{ height: '100%', padding: '6px 12px 12px 12px', minWidth: 150, maxWidth: 200 }}
    >
      <Grid item style={{ fontSize: 14, color: 'gray'}}>
        {label}
      </Grid>

      {values &&
        values.map((parent: BreakdownValue) => (
    <>
     
          <Grid item style={{ fontSize: 12, fontWeight: 550, flexBasis: '100%', paddingTop: 6 }}>
            {parent.label}
          </Grid>
          
            {parent.value &&
              (parent.value as any).map((value: BreakdownValue) => (
                <Grid
                  item
                  container
                  direction="row"
                  alignItems="flex-end"
                  justifyContent="flex-start"
                  spacing={0}
                  style={{ marginLeft: 12,  flexWrap: 'nowrap', whiteSpace: 'nowrap' }}
                >
                  <Grid item style={{ fontSize: 12 }}>
                    {value.label}
                  </Grid>

                  <Grid
                    item
                    container
                    direction="column"
                    alignItems="flex-end"
                    style={{ flexWrap: 'nowrap', cursor: 'pointer', fontSize: 12, fontWeight: 550 }}
                  >
                    {withTooltip(
                      value.label,
                      <span onClick={() => onFilter(value.filter as any)}>
                        {formatBreakdownValue(value)}
                      </span>,
                      'bottom',
                    )}
                    {/*
                / &nbsp;
                {withTooltip(
                  value.label,
                  <span onClick={!value ? undefined : () => onFilter(value.filter as any)}>{}</span>,
                  'bottom',
                )}
                  */}
                  </Grid>
                </Grid>
              ))}

</>
        
        ))}
    </Grid>
  )
}

export default function DistributionHover(
  props: Breakdown & { open: boolean; anchorEl: HTMLElement | null; onClose: (e: any) => void },
) {
  const { anchorEl, onClose, open } = props
  return (
    <Popover
      style={{ pointerEvents: 'none' }}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onClose={onClose}
      disableRestoreFocus
    >
      <DistributionReportCard {...props} />
    </Popover>
  )
}

type HorizontaChartProps = {
  values: AuditResultValues[]
  title?: string
  color?: string
}

function HorizontalChart(props: HorizontaChartProps) {
  let columns = [['Category', 'Value', { role: 'style' }]] // {role: "anotation"}
  const { values, title, color } = props
  let max = 0

  values &&
    values.map((item) => {
      max = Math.max(item.value, max)
      columns.push([item.label, item.value as any, color])
    })

  return (
    <Grid item direction="column" style={{ width: '100%' }}>
      <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>{title}</div>
      {columns.length > 1 ? (
        <Chart
          width={'100%'}
          chartType="BarChart"
          chartEvents={[
            {
              eventName: 'select',
              callback: ({ chartWrapper }: any) => {
                const chart = chartWrapper.getChart()
                const selection: any = chart.getSelection()[0]

                const table = chartWrapper.getDataTable()
                const row: number = selection?.row || 0
                const field = values.filter((a) => a.label === table.getValue(row, 0))[0]

                if (field) {
                  onFilter(field.filter)
                }
              },
            },
          ]}
          loader={<div>Loading...</div>}
          data={columns}
          options={{
            backgroundColor: { fill: 'transparent' } as any,
            chartArea: {
              left: 280,
              width: '60%',
            },
            bar: { groupWidth: '70%' },
            legend: { position: 'none' },
            vAxis: {
              title: '',
              ticks: [],
              baselineColor: 'none',
            },
            hAxis: {
              viewWindowMode: 'explicit',
              viewWindow: {
                max: max,
                min: 0,
              },
              format: '0',
              baselineColor: 'gray',
            },
            titleTextStyle: {
              bold: false,
              color: '#888',
              fontSize: 16,
            },
          }}
        />
      ) : (
        <div
          style={{
            textAlign: 'center',
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          No data found
        </div>
      )}
    </Grid>
  )
}
type ScatterProps = {
  result: AuditScatterValues[]
  title: string
}

function ScatterComponent(props: ScatterProps) {
  let columns = [['Date', 'Audit Score', { type: 'string', role: 'tooltip', p: { html: true } }]]
  const values = props.result || null
  const { title } = props

  values &&
    values?.map((item) => {
      const { serviceDate, percentage } = item
      columns.push([
        utcDateString(serviceDate),
        (percentage * 100) as any,
        `<div style="width: 200px; height: 80px; padding: 15px; font-size: 10pt ">
                                                                              Date: <b>${utcDateString(serviceDate)}</b>
                                                                              <div> Score: <b>${
                                                                                Math.round(percentage * 100) + ' %'
                                                                              }</b></div><div>`,
      ])
    })

  return (
    <>
      <Grid item direction="column" style={{ width: '100%' }}>
        <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>{title}</div>
        {values ? (
          <Chart
            width={'100%'}
            chartType="ScatterChart"
            chartEvents={[
              {
                eventName: 'select',
                callback: ({ chartWrapper }: any) => {
                  const chart = chartWrapper.getChart()
                  const selection: any = chart.getSelection()[0]

                  const table = chartWrapper.getDataTable()
                  const row: number = selection?.row || 0

                  const field = values[row]

                  if (field) {
                    onFilter(field.filter as any)
                  }
                },
              },
            ]}
            loader={<div>Loading...</div>}
            data={columns}
            options={{
              tooltip: { isHtml: true },
              colors: [lightGreen],
              backgroundColor: { fill: 'transparent' } as any,
              chartArea: {
                left: 60,
              },
              legend: { position: 'none' },
              vAxis: {
                minValue: 0,
                maxValue: 100,
                ticks: [0, 20, 40, 60, 80, 100],
                textStyle: {
                  fontSize: 10,
                },
              },
              hAxis: {
                textStyle: {
                  fontSize: 8,
                },
                baselineColor: 'none',
                slantedText: true,
              },
              titleTextStyle: {
                bold: false,
                color: '#888',
                fontSize: 12,
              },
            }}
          />
        ) : (
          <div
            style={{
              textAlign: 'center',
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            No data found
          </div>
        )}
      </Grid>
    </>
  )
}
